import { useEffect, useState } from "react";
import {
  createAndAddUserToGroup,
  getGroupMembersInfo,
  getUsersNotInGroup,
  sendTIMLaunchEmails,
  sendTIMReminderEmails,
  updateGroup,
  updateGroupDeadline,
  FRONT_URL,
} from "../axios/axiosFunctions";
import { DataGrid } from "@mui/x-data-grid";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { columnsGroups } from "../utils/groupsTable";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import PersonIcon from "@mui/icons-material/Person";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SendIcon from "@mui/icons-material/Send";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import TeamCard from "./TeamCard";
import CsvUploadTIM from "./CsvUploadTIM";
import ManageReportAccessDialog from "./ManageReportAccessDialog";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import { slugForCategory, roleLabel } from "../utils/timFamily";

const defaultUser = {
  firstName: "",
  lastName: "",
  email: "",
  type: "",
};

const TeamsCards = ({ group, category, callGroups, onEdit, onDelete }) => {
  // The report slug must match the assessment this group belongs to. Prefer the
  // category threaded from CompaniesTIM, fall back to the group's own category.
  const reportSlug = slugForCategory(category || group?.category);
  const [openDialog, setOpenDialog] = useState(false);
  const [rows, setRows] = useState([{ id: 1 }]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [users, setUsers] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [usersListTypes, setUsersListTypes] = useState({});
  const [selectOpt, setSelectOpt] = useState([]);
  const [error, setError] = useState("");
  const [showError, setShowError] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("error");
  const [msg, setMsg] = useState("");
  const [showMsg, setShowMsg] = useState(false);
  const [msgSeverity, setMsgSeverity] = useState("success");
  const [msg2, setMsg2] = useState("");
  const [showMsg2, setShowMsg2] = useState(false);
  const [msgSeverity2, setMsgSeverity2] = useState("success");
  const [loading, setLoading] = useState(true);
  const [openDialogCreate, setOpenDialogCreate] = useState(false);
  const [userForm, setUserForm] = useState(defaultUser);
  const { firstName, lastName, email, type } = userForm;
  const [groupDeadline, setGroupDeadline] = useState(
    group.deadline ? new Date(group.deadline).toISOString().split("T")[0] : ""
  );
  const [sendEmailsDialogOpen, setSendEmailsDialogOpen] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [sendEmailsAlert, setSendEmailsAlert] = useState(false);
  const [sendEmailsAlertMsg, setSendEmailsAlertMsg] = useState("");
  const [sendEmailsAlertSeverity, setSendEmailsAlertSeverity] = useState("success");
  const [sendRemindersDialogOpen, setSendRemindersDialogOpen] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [sendRemindersAlert, setSendRemindersAlert] = useState(false);
  const [sendRemindersAlertMsg, setSendRemindersAlertMsg] = useState("");
  const [sendRemindersAlertSeverity, setSendRemindersAlertSeverity] = useState("success");
  const [openCsvDialog, setOpenCsvDialog] = useState(false);
  const [openManageAccess, setOpenManageAccess] = useState(false);

  const allCompleted = rows.length > 0 && rows.every((r) => r.status === "Completed");

  useEffect(() => {
    updateTable();
    setGroupDeadline(
      group.deadline ? new Date(group.deadline).toISOString().split("T")[0] : ""
    );
  }, [group]);

  const updateTable = () => {
    setLoading(true);
    callMembersInfo();
    callUsersToAdd();
  };

  const callMembersInfo = async () => {
    const info = await getGroupMembersInfo(group._id);
    const tempRows = info.data.map((u) => ({
      id: u._id,
      firstName: u.firstName || "",
      lastName: u.lastName || "",
      email: u.email,
      type: u.rol,
      status: u.status,
      userId: u._id,
      groupId: group._id,
    }));
    setRows(tempRows);
    setLoading(false);
  };

  const callUsersToAdd = async () => {
    const res = await getUsersNotInGroup(group._id);
    if (res) {
      setUsers(res.users);
      mapOptions(res.users);
    }
  };

  const mapOptions = (usersArray) => {
    if (!usersArray || usersArray.length === 0) {
      setSelectOpt([]);
      return;
    }

    const tempOpt = usersArray.map((u, i) => {
      const fullName = `${u.firstName}${u.lastName ? ` ${u.lastName}` : ""}`;
      return {
        label: u.email ? `${fullName} — ${u.email}` : fullName,
        index: i,
      };
    });
    setSelectOpt(tempOpt);
  };

  const handleOpenDialog = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const showMemberSelect = () => {
    setShowDropdown(!showDropdown);
  };

  const handleChange = (e, newValue) => {
    if (!newValue) return;
    const { index } = newValue;
    const newUsersList = [...usersList];
    const selectedElemet = users[index];

    const newUsersToAdd = [...users.slice(0, index), ...users.slice(index + 1)];

    newUsersList.push(selectedElemet);
    setUsers(newUsersToAdd);
    mapOptions(newUsersToAdd);
    setUsersList(newUsersList);
  };

  const submitNewMembers = async () => {
    if (usersList.length === 0) {
      return;
    }

    const missingType = usersList.some(
      (u) => !["member", "senior", "both"].includes(usersListTypes[u._id])
    );
    if (missingType) {
      setError("Please assign a Type to all members before adding.");
      setShowError(true);
      setAlertSeverity("error");
      setTimeout(() => {
        setShowError(false);
      }, 4000);
      return;
    }

    setLoading(true);

    const usersToAdd = usersList.map((u) => u._id);
    const currentUsers = rows.map((u) => u.userId);

    const membersWithTypes = usersList.map((u) => ({
      userId: u._id,
      type: usersListTypes[u._id],
    }));

    const data = {
      members: usersToAdd.concat(currentUsers),
      membersWithTypes,
    };

    const res = await updateGroup(group._id, data);
    if (!res) {
      setError("Error while saving please try later");
      setShowError(true);
      setAlertSeverity("error");
      setTimeout(() => {
        setShowError(false);
      }, 4000);
      return;
    }
    callMembersInfo();
    callUsersToAdd();
    cancelAddNewMembers();
    callGroups();
  };

  const handleUsersListTypeChange = (userId, newType) => {
    setUsersListTypes((prev) => ({ ...prev, [userId]: newType }));
  };

  const cancelAddNewMembers = () => {
    if (usersList.length === 0) {
      showMemberSelect();
      return;
    }
    setUsersList([]);
    setUsersListTypes({});
    callUsersToAdd();
    showMemberSelect();
  };

  const handleChangeCreate = (e) => {
    const { name, value } = e.target;
    setUserForm({ ...userForm, [name]: value });
  };

  const handlePreviewReport = () => {
    window.open(`/${reportSlug}/${group._id}/report`, '_blank');
  };

  const handleSendLaunchEmails = async () => {
    setSendingEmails(true);
    try {
      const res = await sendTIMLaunchEmails(group._id, { frontUrl: FRONT_URL });
      if (res.error) {
        setSendEmailsAlertSeverity("error");
        setSendEmailsAlertMsg(res.msg || "Error sending emails. Please try again.");
      } else {
        setSendEmailsAlertSeverity("success");
        setSendEmailsAlertMsg(
          `${res.msg || "Emails sent."}${res.sent !== undefined ? ` (${res.sent} sent${res.skippedCompleted ? `, ${res.skippedCompleted} already completed` : ""})` : ""}`
        );
      }
    } catch (err) {
      setSendEmailsAlertSeverity("error");
      setSendEmailsAlertMsg("Error sending emails. Please try again.");
    }
    setSendingEmails(false);
    setSendEmailsAlert(true);
    setSendEmailsDialogOpen(false);
  };

  const handleSendReminderEmails = async () => {
    setSendingReminders(true);
    try {
      const res = await sendTIMReminderEmails(group._id, { frontUrl: FRONT_URL });
      if (res.error) {
        setSendRemindersAlertSeverity("error");
        setSendRemindersAlertMsg(res.msg || "Error sending reminders. Please try again.");
      } else {
        setSendRemindersAlertSeverity("success");
        setSendRemindersAlertMsg(
          `${res.msg || "Reminders sent."}${res.remindersSent !== undefined ? ` (${res.remindersSent} sent, ${res.skippedCompleted} already completed)` : ""}`
        );
      }
    } catch (err) {
      setSendRemindersAlertSeverity("error");
      setSendRemindersAlertMsg("Error sending reminders. Please try again.");
    }
    setSendingReminders(false);
    setSendRemindersAlert(true);
    setSendRemindersDialogOpen(false);
  };

  const handleGroupDeadlineChange = async (e) => {
    const value = e.target.value;
    setGroupDeadline(value);
    await updateGroupDeadline(group._id, { deadline: value || null });
  };

  const validateCreateUser = () => {
    return (
      firstName !== "" &&
      lastName !== "" &&
      email !== "" &&
      type !== ""
    );
  };

  const handleCloseDialogCreate = () => {
    setOpenDialogCreate(false);
  };

  const handleCreateUser = async () => {
    const isValid = validateCreateUser();
    if (!isValid) {
      setMsg2("Complete all fields");
      setShowMsg2(true);
      setMsgSeverity2("error");
      setTimeout(() => {
        setShowMsg2(false);
      }, 2000);
      return;
    }

    const res = await createAndAddUserToGroup(group._id, userForm);
    if (!res.user) {
      setMsg2(res.msg);
      setShowMsg2(true);
      setMsgSeverity2("error");
      setTimeout(() => {
        setShowMsg2(false);
      }, 2000);
      return;
    }
    setMsg2("User created correctly");
    setShowMsg2(true);
    setMsgSeverity2("success");
    setTimeout(() => {
      setShowMsg2(false);
      updateTable();
      callGroups();
      setUserForm(defaultUser);
      handleCloseDialogCreate();
    }, 2000);
    return;
  };

  return (
    <>
      <TeamCard group={group} loading={loading} onClick={handleOpenDialog} onEdit={onEdit} onDelete={onDelete} />
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="md"
        fullWidth
      >
        <DialogContent>
          <Typography variant="h5" sx={{ textAlign: "center" }}>
            {group.name}
          </Typography>
          <Divider sx={{ margin: "10px 0px" }} />
          <Typography variant="h6" sx={{ textAlign: "center" }}>
            MEMBERS
          </Typography>
          <DataGrid
            columns={columnsGroups(category || group?.category)}
            rows={rows.map((row) => ({ ...row, updateTable, callGroups }))}
          />
          {showMsg && (
            <Alert sx={{ marginTop: "10px" }} severity={msgSeverity}>
              {msg}
            </Alert>
          )}
          <Box
            sx={{
              marginTop: "20px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "10px",
            }}
          >
            <TextField
              label="Group Deadline"
              type="date"
              size="small"
              value={groupDeadline}
              onChange={handleGroupDeadlineChange}
              InputLabelProps={{ shrink: true }}
              sx={{ marginRight: "auto" }}
            />
            <Button
              onClick={() => setSendEmailsDialogOpen(true)}
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              disabled={sendingEmails || allCompleted}
            >
              {sendingEmails ? <CircularProgress size={24} color="inherit" /> : "Send Launch Emails"}
            </Button>
            <Button
              onClick={() => setSendRemindersDialogOpen(true)}
              variant="outlined"
              color="warning"
              startIcon={<NotificationsActiveIcon />}
              disabled={sendingReminders || allCompleted}
            >
              {sendingReminders ? <CircularProgress size={24} color="inherit" /> : "Send Reminders"}
            </Button>
            <Button onClick={handlePreviewReport} variant="outlined">
              PREVIEW REPORT
            </Button>
            <Button
              onClick={() => setOpenManageAccess(true)}
              variant="contained"
              color="primary"
              startIcon={<LockOpenIcon />}
              disabled={!allCompleted}
            >
              SEND REPORT
            </Button>
          </Box>
          {sendEmailsAlert && (
            <Alert
              sx={{ marginTop: "10px" }}
              severity={sendEmailsAlertSeverity}
              onClose={() => setSendEmailsAlert(false)}
            >
              {sendEmailsAlertMsg}
            </Alert>
          )}
          {sendRemindersAlert && (
            <Alert
              sx={{ marginTop: "10px" }}
              severity={sendRemindersAlertSeverity}
              onClose={() => setSendRemindersAlert(false)}
            >
              {sendRemindersAlertMsg}
            </Alert>
          )}
          <Typography
            variant="h6"
            sx={{ textAlign: "center", marginTop: "20px" }}
          >
            ADD MEMBERS
          </Typography>
          <Divider sx={{ margin: "10px 0px" }} />
          <Box display={"flex"} justifyContent={"space-evenly"}>
            <Button
              variant="contained"
              startIcon={<CloudUploadIcon />}
              onClick={() => setOpenCsvDialog(true)}
            >
              UPLOAD FILE
            </Button>
            {!showDropdown && (
              <Button onClick={showMemberSelect} variant="contained">
                ADD MEMBER
              </Button>
            )}
          </Box>
          <CsvUploadTIM
            open={openCsvDialog}
            onClose={() => setOpenCsvDialog(false)}
            groupId={group._id}
            groupName={group.name}
            category={category || group?.category}
            onSuccess={() => {
              updateTable();
              callGroups();
            }}
          />
          {showDropdown && (
            <>
              <Button
                onClick={() => setOpenDialogCreate(true)}
                startIcon={<AddCircleOutlineIcon />}
                variant="contained"
              >
                Create User
              </Button>
              <Autocomplete
                onChange={handleChange}
                options={selectOpt}
                renderInput={(params) => (
                  <TextField {...params} label="Search existing user (name or email)" />
                )}
                sx={{ margin: "20px 0px" }}
              />
              {usersList.length === 0 && (
                <Box display={"flex"} justifyContent={"space-evenly"}>
                  <Button onClick={cancelAddNewMembers} variant="contained">
                    CANCEL
                  </Button>
                </Box>
              )}
              <Dialog open={openDialogCreate} onClose={handleCloseDialogCreate}>
                <DialogContent>
                  <Typography variant="h5" color="primary">
                    CREATE USER
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid size={6}>
                      <Typography variant="subtitle1">First Name</Typography>
                      <TextField
                        onChange={handleChangeCreate}
                        value={firstName}
                        name="firstName"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="subtitle1">Last Name</Typography>
                      <TextField
                        onChange={handleChangeCreate}
                        value={lastName}
                        name="lastName"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="subtitle1">Email</Typography>
                      <TextField
                        onChange={handleChangeCreate}
                        value={email}
                        name="email"
                        fullWidth
                      />
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="subtitle1">Type</Typography>
                      <FormControl sx={{ width: "100%" }}>
                        <Select
                          onChange={handleChangeCreate}
                          value={type}
                          name="type"
                          fullWidth
                        >
                          <MenuItem value={"member"}>{roleLabel("member", category || group?.category)}</MenuItem>
                          <MenuItem value={"senior"}>{roleLabel("senior", category || group?.category)}</MenuItem>
                          {(category || group?.category) !== "executive" && (
                            <MenuItem value={"both"}>{roleLabel("both", category || group?.category)}</MenuItem>
                          )}
                        </Select>
                      </FormControl>
                    </Grid>
                    {showMsg2 && (
                      <Grid size={12}>
                        <Alert
                          sx={{ marginTop: "20px" }}
                          severity={msgSeverity2}
                        >
                          {msg2}
                        </Alert>
                      </Grid>
                    )}
                    <Grid
                      size={12}
                      sx={{ display: "flex", justifyContent: "center" }}
                    >
                      <Button onClick={handleCreateUser} variant="contained">
                        CREATE
                      </Button>
                      <Button
                        variant="contained"
                        onClick={handleCloseDialogCreate}
                        sx={{ marginLeft: "20px" }}
                      >
                        CANCEL
                      </Button>
                    </Grid>
                  </Grid>
                </DialogContent>
              </Dialog>
            </>
          )}
          {usersList.length > 0 && (
            <>
              <Typography
                variant="subtitle1"
                fontWeight={500}
                color="secondary"
                sx={{ marginTop: "20px" }}
              >
                Users to Add
              </Typography>
              <List>
                {usersList.map((u, i) => (
                  <ListItem key={i} sx={{ gap: 2 }}>
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${u.firstName}${u.lastName ? ` ${u.lastName}` : ""}`}
                    />
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                      <Select
                        value={usersListTypes[u._id] || ""}
                        onChange={(e) => handleUsersListTypeChange(u._id, e.target.value)}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>Select Type *</MenuItem>
                        <MenuItem value="member">{roleLabel("member", category || group?.category)}</MenuItem>
                        <MenuItem value="senior">{roleLabel("senior", category || group?.category)}</MenuItem>
                        {(category || group?.category) !== "executive" && (
                          <MenuItem value="both">{roleLabel("both", category || group?.category)}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                  </ListItem>
                ))}
              </List>
              {showError && (
                <Alert sx={{ marginTop: "20px" }} severity={alertSeverity}>
                  {error}
                </Alert>
              )}
              <Box display={"flex"} justifyContent={"space-evenly"}>
                <Button onClick={submitNewMembers} variant="contained">
                  ADD
                </Button>
                <Button onClick={cancelAddNewMembers} variant="contained">
                  CANCEL
                </Button>
              </Box>
            </>
          )}
          <Box
            sx={{ display: "flex", margin: "20px", justifyContent: "center" }}
          >
            <Button
              variant="contained"
              onClick={handleDialogClose}
              color="customGray"
            >
              CLOSE
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Manage Report Access Dialog (per-recipient shares) */}
      <ManageReportAccessDialog
        open={openManageAccess}
        onClose={() => setOpenManageAccess(false)}
        group={group}
        assessmentSlug={reportSlug}
        teamMembers={rows.map((r) => ({
          _id: r.userId,
          firstName: r.firstName,
          lastName: r.lastName,
          email: r.email,
        }))}
      />

      {/* Send Launch Emails Confirmation Dialog */}
      <Dialog open={sendEmailsDialogOpen} onClose={() => setSendEmailsDialogOpen(false)}>
        <DialogTitle>Send Launch Emails</DialogTitle>
        <DialogContent>
          <Typography>
            This will send the Teams in Motion<sup>®</sup> invitation email to every member of <strong>{group.name}</strong>.
          </Typography>
          {!groupDeadline && (
            <Typography sx={{ marginTop: "10px", color: "#b26500" }}>
              No deadline is set yet. Members will receive the launch email without a due date — automatic reminders won’t fire until you set one.
            </Typography>
          )}
          <Typography sx={{ marginTop: "10px", fontWeight: 600 }}>
            Are you sure you want to send the launch emails?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendEmailsDialogOpen(false)} color="inherit">
            CANCEL
          </Button>
          <Button onClick={handleSendLaunchEmails} variant="contained" color="primary">
            YES, SEND LAUNCH EMAILS
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Reminders Confirmation Dialog */}
      <Dialog open={sendRemindersDialogOpen} onClose={() => setSendRemindersDialogOpen(false)}>
        <DialogTitle>Send Reminder Emails</DialogTitle>
        <DialogContent>
          <Typography>
            This will email a reminder to every member of <strong>{group.name}</strong> who hasn’t completed their assessment yet. Members who are already done will be skipped.
          </Typography>
          <Typography sx={{ marginTop: "10px", fontWeight: 600 }}>
            Send reminders now?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendRemindersDialogOpen(false)} color="inherit">
            CANCEL
          </Button>
          <Button onClick={handleSendReminderEmails} variant="contained" color="warning">
            YES, SEND REMINDERS
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TeamsCards;
